import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useEvents } from '../contexts/EventContext';
import { MODULE_CATALOG } from '../config/moduleCatalog';

/**
 * "Choose modules" — the per-event module configuration screen.
 *
 * Shows one toggle card per module the tenant is allowed to use (the ceiling,
 * returned by GET /events/:id/modules). Toggling calls PUT and refreshes the
 * EventContext so the sidebar nav updates immediately. Requires an event to be
 * in scope.
 */

interface ModuleState {
  key: string;
  enabled: boolean;
}

export function EventModulesPage() {
  const navigate = useNavigate();
  const { selectedEventId, selectedEvent, refreshModules } = useEvents();
  const [modules, setModules] = useState<ModuleState[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!selectedEventId) { setLoading(false); return; }
      setLoading(true);
      try {
        const res = await apiClient<{ modules: ModuleState[] }>(`/events/${selectedEventId}/modules`);
        if (!cancelled) { setModules(res.modules || []); setError(''); }
      } catch {
        if (!cancelled) setError('Could not load modules for this event.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedEventId]);

  async function toggle(key: string, next: boolean) {
    if (!selectedEventId) return;
    // Turning ticketing OFF makes this a non-ticketed (free) event: orders and
    // pricing tabs disappear. Data is hidden, never deleted — re-enabling
    // restores it. Confirm so it isn't flipped by accident.
    if (key === 'ticketing' && !next) {
      const ok = window.confirm(
        'Turn off ticketing for this event?\n\n' +
          'This makes it a non-ticketed (free) event. Pricing tiers and orders ' +
          'will be hidden, but nothing is deleted — turning ticketing back on ' +
          'restores them. Guests and seating are unaffected.',
      );
      if (!ok) return;
    }
    setSavingKey(key);
    setError('');
    // Optimistic update.
    setModules((prev) => prev.map((m) => (m.key === key ? { ...m, enabled: next } : m)));
    try {
      const res = await apiClient<{ modules: ModuleState[] }>(`/events/${selectedEventId}/modules`, {
        method: 'PUT',
        body: { moduleKey: key, enabled: next },
      });
      setModules(res.modules || []);
      await refreshModules(); // update the sidebar nav
    } catch (e: any) {
      // Revert on failure.
      setModules((prev) => prev.map((m) => (m.key === key ? { ...m, enabled: !next } : m)));
      setError(e?.message || 'Could not update that module.');
    } finally {
      setSavingKey(null);
    }
  }

  if (!selectedEventId) {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>Modules</h1>
        <div style={styles.emptyCard}>
          <p style={{ margin: 0, fontWeight: 600, color: '#0f2d1f' }}>No event selected.</p>
          <p style={styles.muted}>Pick an event in the sidebar “Active event” picker to configure its modules.</p>
          <button style={styles.btn} onClick={() => navigate('/dashboard')}>Go to events</button>
        </div>
      </div>
    );
  }

  const defFor = (key: string) => MODULE_CATALOG.find((m) => m.key === key);
  const enabledCount = modules.filter((m) => m.enabled).length;

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Modules</h1>
      <p style={styles.subtitle}>
        Turn tools on or off for <strong>{selectedEvent?.name || 'this event'}</strong>.
        {' '}Only modules included in your plan appear here. {enabledCount} enabled.
      </p>

      {error && <div style={styles.error} onClick={() => setError('')}>{error} <span style={{ float: 'right', fontWeight: 700 }}>×</span></div>}

      {loading ? (
        <p style={styles.muted}>Loading modules…</p>
      ) : modules.length === 0 ? (
        <div style={styles.emptyCard}>
          <p style={{ margin: 0, fontWeight: 600, color: '#0f2d1f' }}>No modules available.</p>
          <p style={styles.muted}>Your plan doesn’t include any modules yet. Contact your platform administrator.</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {modules.map((m) => {
            const def = defFor(m.key);
            const busy = savingKey === m.key;
            return (
              <div key={m.key} style={{ ...styles.card, ...(m.enabled ? styles.cardOn : {}) }}>
                <div style={styles.cardHead}>
                  <span style={styles.cardIcon}>{def?.icon || '🧩'}</span>
                  <span style={styles.cardLabel}>{def?.label || m.key}</span>
                  <button
                    role="switch"
                    aria-checked={m.enabled}
                    aria-label={`${m.enabled ? 'Disable' : 'Enable'} ${def?.label || m.key}`}
                    disabled={busy}
                    style={{ ...styles.switch, ...(m.enabled ? styles.switchOn : {}) }}
                    onClick={() => toggle(m.key, !m.enabled)}
                  >
                    <span style={{ ...styles.knob, ...(m.enabled ? styles.knobOn : {}) }} />
                  </button>
                </div>
                <p style={styles.cardDesc}>{def?.description || ''}</p>
                {def?.dependsOn && def.dependsOn.length > 0 && (
                  <p style={styles.dep}>Needs: {def.dependsOn.join(', ')}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1000px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 0.25rem 0' },
  subtitle: { color: '#666', fontSize: '0.9rem', marginBottom: '1.5rem' },
  muted: { color: '#888', fontSize: '0.9rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' },
  card: {
    padding: '1.1rem', backgroundColor: '#fff', border: '1px solid #e0e0e0',
    borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardOn: { borderColor: '#1a6b3c', boxShadow: '0 1px 6px rgba(26,107,60,0.15)' },
  cardHead: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  cardIcon: { fontSize: '1.3rem' },
  cardLabel: { fontSize: '1rem', fontWeight: 700, color: '#0f2d1f', flex: 1 },
  cardDesc: { fontSize: '0.82rem', color: '#555', margin: '0.6rem 0 0 0', lineHeight: 1.4 },
  dep: { fontSize: '0.72rem', color: '#999', margin: '0.4rem 0 0 0' },
  switch: {
    width: '42px', height: '24px', borderRadius: '12px', border: 'none',
    backgroundColor: '#ccc', cursor: 'pointer', position: 'relative', flexShrink: 0,
    transition: 'background-color 0.2s', padding: 0,
  },
  switchOn: { backgroundColor: '#1a6b3c' },
  knob: {
    position: 'absolute', top: '2px', left: '2px', width: '20px', height: '20px',
    borderRadius: '50%', backgroundColor: '#fff', transition: 'left 0.2s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  knobOn: { left: '20px' },
  emptyCard: {
    padding: '2rem', backgroundColor: '#fff', border: '1px dashed #c9a84c',
    borderRadius: '10px', textAlign: 'center',
  },
  btn: {
    marginTop: '0.75rem', padding: '0.55rem 1rem', backgroundColor: '#1a6b3c', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
  },
  error: {
    color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.6rem 0.9rem',
    borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem', cursor: 'pointer',
  },
};
